import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Phone, CheckCircle, Clock, Shield, Truck, MessageCircle, Star, 
  ArrowRight, MapPin, Package, Weight, Calendar, Info, Loader2,
  Search, Plus, Minus, ChevronDown, ChevronRight, AlertTriangle,
  Box, Armchair, Bed, UtensilsCrossed, Monitor, Car, X, Calculator
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { mockData } from '../mock';
import { businessConfig } from '../config/business';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category icons mapping
const CATEGORY_ICONS = {
  'bedroom': Bed,
  'living': Armchair,
  'kitchen': UtensilsCrossed,
  'dining': UtensilsCrossed,
  'office': Monitor,
  'garage': Car,
  'outdoor': Car,
  'boxes': Box,
  'default': Package
};

function getCategoryIcon(categoryName) {
  const name = categoryName.toLowerCase();
  for (const [key, Icon] of Object.entries(CATEGORY_ICONS)) {
    if (name.includes(key)) return Icon;
  }
  return CATEGORY_ICONS.default;
}

// Google Places Autocomplete Component - Using Places API (New) with AutocompleteSuggestion
function AddressAutocomplete({ id, value, onChange, placeholder, label, required = true }) {
  const [inputValue, setInputValue] = useState(typeof value === 'object' ? value.formatted_address : value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const [placesLoaded, setPlacesLoaded] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  // Update input value when prop changes
  useEffect(() => {
    const newValue = typeof value === 'object' ? value.formatted_address : value || '';
    setInputValue(newValue);
  }, [value]);

  // Initialize Places library and session token
  useEffect(() => {
    let isMounted = true;

    const initPlaces = async () => {
      try {
        if (!window.google?.maps?.importLibrary) {
          setTimeout(() => {
            if (isMounted) initPlaces();
          }, 200);
          return;
        }

        // Import the places library
        const { AutocompleteSessionToken } = await window.google.maps.importLibrary('places');
        
        if (isMounted) {
          setSessionToken(new AutocompleteSessionToken());
          setPlacesLoaded(true);
        }
      } catch (err) {
        console.error('Error initializing Places:', err);
      }
    };

    initPlaces();

    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch suggestions using the new AutocompleteSuggestion API
  const fetchSuggestions = useCallback(async (input) => {
    if (!placesLoaded || input.length < 3) {
      setSuggestions([]);
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    setIsLoading(true);

    try {
      const { AutocompleteSuggestion } = await window.google.maps.importLibrary('places');
      
      const request = {
        input: input,
        sessionToken: sessionToken,
        includedRegionCodes: ['au'], // Restrict to Australia
        includedPrimaryTypes: ['street_address', 'subpremise', 'premise']
      };

      const { suggestions: newSuggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      
      // Only update if this is still the latest request
      if (currentRequestId === requestIdRef.current) {
        setSuggestions(newSuggestions || []);
        setShowSuggestions((newSuggestions || []).length > 0);
      }
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      if (currentRequestId === requestIdRef.current) {
        setSuggestions([]);
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [placesLoaded, sessionToken]);

  // Handle input change with debounced autocomplete
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange({
      formatted_address: newValue,
      place_id: null,
      lat: null,
      lng: null
    });

    // Debounce autocomplete requests
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  // Handle suggestion selection
  const handleSelectSuggestion = async (suggestion) => {
    try {
      const placePrediction = suggestion.placePrediction;
      const place = placePrediction.toPlace();
      
      // Fetch place details - this concludes the session
      await place.fetchFields({
        fields: ['formattedAddress', 'location', 'id']
      });

      const location = place.location;
      const formattedAddress = place.formattedAddress || placePrediction.text?.text || '';
      
      setInputValue(formattedAddress);
      onChange({
        formatted_address: formattedAddress,
        place_id: place.id || null,
        lat: location?.lat() || null,
        lng: location?.lng() || null
      });

      // Create a new session token for the next search
      const { AutocompleteSessionToken } = await window.google.maps.importLibrary('places');
      setSessionToken(new AutocompleteSessionToken());
      
    } catch (err) {
      console.error('Error selecting place:', err);
      // Fallback - use the text from prediction
      const text = suggestion.placePrediction?.text?.text || '';
      setInputValue(text);
      onChange({
        formatted_address: text,
        place_id: null,
        lat: null,
        lng: null
      });
    }

    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <label htmlFor={id} className="block text-sm font-semibold text-gray-700 mb-1">
        {label} {required && '*'}
      </label>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
        <input
          ref={inputRef}
          id={id}
          type="text"
          required={required}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 focus:outline-none"
          placeholder={placeholder}
          autoComplete="off"
          data-testid={`address-input-${id}`}
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
      </div>
      
      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          data-testid={`address-suggestions-${id}`}
        >
          {suggestions.map((suggestion, index) => {
            const prediction = suggestion.placePrediction;
            const mainText = prediction?.mainText?.text || prediction?.text?.text || '';
            const secondaryText = prediction?.secondaryText?.text || '';
            
            return (
              <button
                key={index}
                type="button"
                onClick={() => handleSelectSuggestion(suggestion)}
                className="w-full text-left px-4 py-3 hover:bg-yellow-50 focus:bg-yellow-50 focus:outline-none border-b border-gray-100 last:border-b-0"
              >
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{mainText}</p>
                    {secondaryText && (
                      <p className="text-xs text-gray-500">{secondaryText}</p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
          <div className="px-4 py-2 text-xs text-gray-400 border-t border-gray-100">
            Powered by Google
          </div>
        </div>
      )}
    </div>
  );
}

// Inventory Item Row Component
function InventoryItemRow({ item, quantity, onQuantityChange }) {
  return (
    <div className="flex items-center justify-between py-2 px-3 hover:bg-gray-50 rounded-lg">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
        {item.volume > 0 && (
          <p className="text-xs text-gray-500">{item.volume} m³ each</p>
        )}
      </div>
      <div className="flex items-center gap-2 ml-4">
        <button
          type="button"
          onClick={() => onQuantityChange(Math.max(0, quantity - 1))}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600"
          data-testid={`item-minus-${item.id}`}
        >
          <Minus className="w-4 h-4" />
        </button>
        <span className="w-8 text-center font-semibold text-gray-900" data-testid={`item-qty-${item.id}`}>
          {quantity}
        </span>
        <button
          type="button"
          onClick={() => onQuantityChange(quantity + 1)}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-yellow-100 hover:bg-yellow-200 text-yellow-700"
          data-testid={`item-plus-${item.id}`}
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// Inventory Category Accordion
function InventoryCategory({ category, selectedItems, onItemChange, searchQuery }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return category.options || [];
    const query = searchQuery.toLowerCase();
    return (category.options || []).filter(opt => 
      opt.name.toLowerCase().includes(query)
    );
  }, [category.options, searchQuery]);

  const selectedCount = useMemo(() => {
    return (category.options || []).reduce((count, opt) => {
      return count + (selectedItems[opt.id] || 0);
    }, 0);
  }, [category.options, selectedItems]);

  const CategoryIcon = getCategoryIcon(category.name);

  // Auto-expand if search matches items in this category
  useEffect(() => {
    if (searchQuery && filteredOptions.length > 0) {
      setIsExpanded(true);
    }
  }, [searchQuery, filteredOptions.length]);

  if (filteredOptions.length === 0 && searchQuery) return null;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors"
        data-testid={`category-${category.id}`}
      >
        <div className="flex items-center gap-3">
          <CategoryIcon className="w-5 h-5 text-yellow-600" />
          <span className="font-semibold text-gray-900">{category.name}</span>
          {selectedCount > 0 && (
            <span className="bg-yellow-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {selectedCount}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-400" />
        )}
      </button>
      
      {isExpanded && (
        <div className="border-t border-gray-100 bg-gray-50 p-2 max-h-64 overflow-y-auto">
          {filteredOptions.map(item => (
            <InventoryItemRow
              key={item.id}
              item={item}
              quantity={selectedItems[item.id] || 0}
              onQuantityChange={(qty) => onItemChange(item, qty)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Selected Items Summary
function SelectedItemsSummary({ categories, selectedItems, onItemChange }) {
  const selectedList = useMemo(() => {
    const items = [];
    categories.forEach(cat => {
      (cat.options || []).forEach(opt => {
        if (selectedItems[opt.id] > 0) {
          items.push({
            ...opt,
            categoryName: cat.name,
            quantity: selectedItems[opt.id]
          });
        }
      });
    });
    return items;
  }, [categories, selectedItems]);

  if (selectedList.length === 0) return null;

  return (
    <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <CheckCircle className="w-5 h-5 text-green-600" />
        Your Selected Items ({selectedList.length})
      </h4>
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {selectedList.map(item => (
          <div key={item.id} className="flex items-center justify-between text-sm">
            <span className="text-gray-700">
              {item.name} <span className="text-gray-500">× {item.quantity}</span>
            </span>
            <button
              type="button"
              onClick={() => onItemChange(item, 0)}
              className="text-red-500 hover:text-red-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Heavy Items Selector
function HeavyItemsSelector({ heavyItems, setHeavyItems }) {
  const [showHeavyItems, setShowHeavyItems] = useState(false);
  
  const heavyItemOptions = [
    'Piano (upright)',
    'Piano (grand)',
    'Safe',
    'Pool table',
    'Gym equipment (heavy)',
    'Commercial equipment',
    'Other heavy item'
  ];

  const addHeavyItem = (name) => {
    const existing = heavyItems.find(h => h.name === name);
    if (existing) {
      setHeavyItems(heavyItems.map(h => 
        h.name === name ? { ...h, qty: h.qty + 1 } : h
      ));
    } else {
      setHeavyItems([...heavyItems, { name, qty: 1 }]);
    }
  };

  const removeHeavyItem = (name) => {
    const existing = heavyItems.find(h => h.name === name);
    if (existing && existing.qty > 1) {
      setHeavyItems(heavyItems.map(h => 
        h.name === name ? { ...h, qty: h.qty - 1 } : h
      ));
    } else {
      setHeavyItems(heavyItems.filter(h => h.name !== name));
    }
  };

  const totalHeavyItems = heavyItems.reduce((sum, h) => sum + h.qty, 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-semibold text-gray-700">
          Do you have any items over 90kg?
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowHeavyItems(false)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              !showHeavyItems 
                ? 'bg-gray-900 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            No
          </button>
          <button
            type="button"
            onClick={() => setShowHeavyItems(true)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              showHeavyItems 
                ? 'bg-yellow-500 text-gray-900' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Yes
          </button>
        </div>
      </div>

      {showHeavyItems && (
        <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
          <p className="text-sm text-orange-800 mb-3">
            <Weight className="w-4 h-4 inline mr-1" />
            Heavy items over 90kg have a $80 surcharge each
          </p>
          
          <div className="grid grid-cols-2 gap-2 mb-3">
            {heavyItemOptions.map(option => {
              const item = heavyItems.find(h => h.name === option);
              const qty = item?.qty || 0;
              
              return (
                <div key={option} className="flex items-center justify-between p-2 bg-white rounded border">
                  <span className="text-sm text-gray-700 truncate flex-1">{option}</span>
                  <div className="flex items-center gap-1 ml-2">
                    <button
                      type="button"
                      onClick={() => removeHeavyItem(option)}
                      className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 hover:bg-gray-200"
                      disabled={qty === 0}
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-6 text-center text-sm font-semibold">{qty}</span>
                    <button
                      type="button"
                      onClick={() => addHeavyItem(option)}
                      className="w-6 h-6 flex items-center justify-center rounded bg-yellow-100 hover:bg-yellow-200"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {totalHeavyItems > 0 && (
            <p className="text-sm font-semibold text-orange-900">
              Total heavy items: {totalHeavyItems} (${totalHeavyItems * 80} surcharge)
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// Access Info Selector
function AccessInfoSelector({ accessTags, pickupAccess, setPickupAccess, deliveryAccess, setDeliveryAccess }) {
  if (!accessTags || accessTags.length === 0) return null;

  const renderAccessTag = (tag, access, setAccess, prefix) => {
    const currentValue = access.find(a => a.access_tag_id === tag.id);
    
    if (tag.requires_input) {
      return (
        <div key={tag.id} className="flex items-center gap-2">
          <label className="text-sm text-gray-700 flex-1">{tag.item}</label>
          <input
            type="number"
            min="0"
            placeholder={tag.unit || ''}
            value={currentValue?.value || ''}
            onChange={(e) => {
              const newValue = e.target.value;
              if (newValue) {
                setAccess([
                  ...access.filter(a => a.access_tag_id !== tag.id),
                  { access_tag_id: tag.id, value: newValue }
                ]);
              } else {
                setAccess(access.filter(a => a.access_tag_id !== tag.id));
              }
            }}
            className="w-20 px-2 py-1 text-sm border rounded"
            data-testid={`${prefix}-access-${tag.id}`}
          />
          {tag.unit && <span className="text-xs text-gray-500">{tag.unit}</span>}
        </div>
      );
    } else {
      return (
        <label key={tag.id} className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!currentValue}
            onChange={(e) => {
              if (e.target.checked) {
                setAccess([...access, { access_tag_id: tag.id, value: null }]);
              } else {
                setAccess(access.filter(a => a.access_tag_id !== tag.id));
              }
            }}
            className="w-4 h-4 text-yellow-500 rounded"
            data-testid={`${prefix}-access-${tag.id}`}
          />
          <span className="text-sm text-gray-700">{tag.item}</span>
        </label>
      );
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="p-4 bg-gray-50 rounded-lg">
        <h4 className="font-semibold text-gray-900 mb-3">Pickup Access</h4>
        <div className="space-y-2">
          {accessTags.map(tag => renderAccessTag(tag, pickupAccess, setPickupAccess, 'pickup'))}
        </div>
      </div>
      <div className="p-4 bg-gray-50 rounded-lg">
        <h4 className="font-semibold text-gray-900 mb-3">Delivery Access</h4>
        <div className="space-y-2">
          {accessTags.map(tag => renderAccessTag(tag, deliveryAccess, setDeliveryAccess, 'delivery'))}
        </div>
      </div>
      <p className="col-span-full text-xs text-gray-500">
        Note: Access information helps us plan your move. No extra charges for stairs.
      </p>
    </div>
  );
}

// Simplified Access Tag Selector for Quick Quote flow
function AccessTagSelector({ tags, selectedTags, onChange, location }) {
  if (!tags || tags.length === 0) return null;

  const handleTagChange = (tag, value) => {
    if (value === null || value === '' || value === false) {
      // Remove tag
      onChange(selectedTags.filter(t => t.access_tag_id !== tag.id));
    } else {
      // Add or update tag
      const existing = selectedTags.find(t => t.access_tag_id === tag.id);
      if (existing) {
        onChange(selectedTags.map(t => 
          t.access_tag_id === tag.id ? { ...t, value: value } : t
        ));
      } else {
        onChange([...selectedTags, { access_tag_id: tag.id, value: tag.requires_input ? value : null }]);
      }
    }
  };

  return (
    <div className="space-y-2">
      {tags.map(tag => {
        const currentValue = selectedTags.find(t => t.access_tag_id === tag.id);
        
        if (tag.requires_input) {
          return (
            <div key={tag.id} className="flex items-center gap-2">
              <label className="text-sm text-gray-700 flex-1">{tag.item}</label>
              <input
                type="number"
                min="0"
                placeholder={tag.unit || ''}
                value={currentValue?.value || ''}
                onChange={(e) => handleTagChange(tag, e.target.value || null)}
                className="w-20 px-2 py-1 text-sm border rounded focus:border-yellow-500 focus:outline-none"
                data-testid={`${location}-access-${tag.id}`}
              />
              {tag.unit && <span className="text-xs text-gray-500">{tag.unit}</span>}
            </div>
          );
        } else {
          return (
            <label key={tag.id} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!currentValue}
                onChange={(e) => handleTagChange(tag, e.target.checked ? true : null)}
                className="w-4 h-4 text-yellow-500 rounded focus:ring-yellow-500"
                data-testid={`${location}-access-${tag.id}`}
              />
              <span className="text-sm text-gray-700">{tag.item}</span>
            </label>
          );
        }
      })}
    </div>
  );
}

// Quote Summary Component
function QuoteSummary({ quote, isLoading, itemCount, totalCbm, inventorySkipped }) {
  // Handle inventory skipped case
  if (inventorySkipped) {
    return (
      <Card className="border-2 border-blue-400 bg-blue-50" data-testid="quote-summary-skipped">
        <CardContent className="pt-6">
          <h3 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-2">
            <Phone className="w-6 h-6 text-blue-600" />
            Quote Request
          </h3>
          
          <div className="space-y-4">
            <div className="pb-3 border-b border-blue-200">
              <span className="text-lg font-semibold text-gray-900">Rate to be confirmed</span>
              <p className="text-sm text-gray-600 mt-1">
                We'll confirm the appropriate truck capacity and hourly rate after reviewing your move details.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-3">
              <p className="text-sm text-gray-700">
                <strong>What happens next:</strong>
              </p>
              <ul className="text-sm text-gray-600 mt-2 space-y-1">
                <li>• We'll call to discuss your move</li>
                <li>• Confirm inventory and volume</li>
                <li>• Provide accurate pricing</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className="border-2 border-yellow-400 bg-yellow-50">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-yellow-600" />
            <span className="ml-2 text-gray-700">Calculating your quote...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!quote) {
    return (
      <Card className="border-2 border-gray-200 bg-gray-50">
        <CardContent className="pt-6">
          <div className="text-center py-8 text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Add items and enter your addresses to see your instant quote</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (quote.requires_tailored_quote) {
    return (
      <Card className="border-2 border-orange-400 bg-orange-50">
        <CardContent className="pt-6">
          <div className="text-center py-4">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-orange-500" />
            <h3 className="font-bold text-lg text-gray-900 mb-2">Large Move</h3>
            <p className="text-gray-700 mb-4">
              Your move is over 52m³ - contact us for a tailored quote
            </p>
            <p className="text-2xl font-bold text-gray-900">{totalCbm.toFixed(1)} m³</p>
            <p className="text-sm text-gray-500">{itemCount} items</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-yellow-400 bg-yellow-50" data-testid="quote-summary">
      <CardContent className="pt-6">
        <h3 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-2">
          <CheckCircle className="w-6 h-6 text-green-600" />
          Your Instant Quote
        </h3>

        <div className="space-y-4">
          {/* Volume Summary - Show actual volume, not tier */}
          <div className="flex justify-between items-center pb-3 border-b border-yellow-300">
            <div>
              <span className="font-semibold text-gray-900">Estimated Volume</span>
              <p className="text-xs text-gray-500">{itemCount} items selected</p>
            </div>
            <span className="text-xl font-bold text-gray-900" data-testid="total-cbm">
              {quote.volume_cbm?.toFixed(1)} m³
            </span>
          </div>

          {/* Hourly Rate - Without tier label */}
          <div className="flex justify-between items-center">
            <div>
              <span className="font-semibold text-gray-900">Hourly Rate</span>
              <p className="text-xs text-gray-600">(includes 2 removalists)</p>
            </div>
            <span className="text-2xl font-bold text-gray-900" data-testid="hourly-rate">
              ${quote.hourly_rate?.toFixed(2)}/hr
            </span>
          </div>

          {/* Minimum Booking */}
          <div className="flex justify-between items-center text-sm text-gray-700">
            <span>Minimum booking</span>
            <span className="font-semibold">{quote.minimum_hours} hours</span>
          </div>

          {/* Callout Fee */}
          <div className="flex justify-between items-center text-sm text-gray-700">
            <div>
              <span>Call-out fee</span>
              <p className="text-xs text-gray-500">
                (Travel to pickup location)
              </p>
            </div>
            <span className="font-semibold" data-testid="callout-fee">
              ${quote.callout_fee?.toFixed(2)}
            </span>
          </div>

          {/* Heavy Items Surcharge */}
          {quote.heavy_items_count > 0 && (
            <div className="flex justify-between items-center text-sm text-gray-700">
              <div>
                <span>Heavy items surcharge</span>
                <p className="text-xs text-gray-500">
                  ({quote.heavy_items_count} item{quote.heavy_items_count > 1 ? 's' : ''} over 90kg @ $80 each)
                </p>
              </div>
              <span className="font-semibold text-orange-600" data-testid="heavy-surcharge">
                +${quote.heavy_item_surcharge?.toFixed(2)}
              </span>
            </div>
          )}

          {/* Sunday Surcharge */}
          {quote.is_sunday && (
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-700">Sunday surcharge</span>
              <span className="font-semibold text-orange-600" data-testid="sunday-surcharge">
                +${quote.sunday_surcharge_per_hour}/hr included
              </span>
            </div>
          )}

          {/* Midweek Discount - DISCONTINUED (code preserved but never displays) */}
          {/* Note: midweek discount removed December 2025 - is_midweek always false now */}

          {/* Disclaimer */}
          <div className="mt-4 p-3 bg-white rounded-lg text-sm text-gray-600">
            <p>Final cost depends on actual time worked. Charged in 30-minute increments after the minimum 2 hours.</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Main Quote Page Component
export default function QuotePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Inventory state
  const [inventoryCategories, setInventoryCategories] = useState([]);
  const [accessTags, setAccessTags] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingInventory, setIsLoadingInventory] = useState(true);
  const [blockedDates, setBlockedDates] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    moveDate: '',
    pickupAddress: { formatted_address: '', place_id: null, lat: null, lng: null },
    deliveryAddress: { formatted_address: '', place_id: null, lat: null, lng: null },
    notes: ''
  });
  
  // Access info
  const [pickupAccess, setPickupAccess] = useState([]);
  const [deliveryAccess, setDeliveryAccess] = useState([]);
  
  // Heavy items
  const [heavyItems, setHeavyItems] = useState([]);
  
  // Custom items (items not in MovePro catalogue)
  const [customItems, setCustomItems] = useState([]);
  const [showCustomItemForm, setShowCustomItemForm] = useState(false);
  const [newCustomItem, setNewCustomItem] = useState({ name: '', qty: 1, volume: 0.5 });
  
  // Inventory skipped option
  const [inventorySkipped, setInventorySkipped] = useState(false);
  
  // Quote mode: null = choice screen, 'instant' = detailed inventory, 'quick' = quick quote
  const [quoteMode, setQuoteMode] = useState(null);
  
  // Quick quote move size (qualification data only, NOT for pricing)
  const [moveSize, setMoveSize] = useState('');
  
  // Move size options for Quick Quote
  const moveSizeOptions = [
    { value: 'few_items', label: 'Few items', description: 'Individual furniture or small load' },
    { value: 'studio_1bed', label: 'Studio / 1 bedroom', description: 'Compact living space' },
    { value: '2bed', label: '2 bedroom', description: 'Standard apartment or unit' },
    { value: '3bed', label: '3 bedroom', description: 'Family home' },
    { value: '4bed_plus', label: '4+ bedroom', description: 'Large family home' },
    { value: 'office', label: 'Office', description: 'Business or office space' },
    { value: 'not_sure', label: 'Not sure', description: 'We\'ll help you work it out' }
  ];
  
  // Quote state
  const [quote, setQuote] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Email validation
  const [emailError, setEmailError] = useState('');
  
  // Email validation function
  const validateEmail = (email) => {
    if (!email || email.trim() === '') {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };
  
  // Handle email change with validation
  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setFormData(prev => ({ ...prev, email: newEmail }));
    // Clear error when user starts typing
    if (emailError) {
      setEmailError('');
    }
  };
  
  // Validate email on blur
  const handleEmailBlur = () => {
    const error = validateEmail(formData.email);
    setEmailError(error);
  };
  
  const calculateTimeoutRef = useRef(null);

  // Get marketing attribution from URL params
  const attribution = useMemo(() => ({
    utm_source: searchParams.get('utm_source'),
    utm_medium: searchParams.get('utm_medium'),
    utm_campaign: searchParams.get('utm_campaign'),
    utm_term: searchParams.get('utm_term'),
    utm_content: searchParams.get('utm_content'),
    gclid: searchParams.get('gclid')
  }), [searchParams]);

  // Fetch inventory and access tags on mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingInventory(true);
      try {
        const [inventoryRes, tagsRes, datesRes] = await Promise.all([
          axios.get(`${API}/movepro/inventory`).catch(() => ({ data: { categories: [] } })),
          axios.get(`${API}/movepro/access-tags`).catch(() => ({ data: { tags: [] } })),
          axios.get(`${API}/availability/unavailable-dates`).catch(() => ({ data: { unavailable_dates: [] } }))
        ]);
        
        // Filter out the "Custom" category from MovePro inventory (handled by our custom item form instead)
        const filteredCategories = (inventoryRes.data.categories || []).filter(
          cat => cat.name?.toLowerCase() !== 'custom'
        );
        setInventoryCategories(filteredCategories);
        setAccessTags(tagsRes.data.tags || []);
        setBlockedDates(datesRes.data.unavailable_dates || []);
      } catch (error) {
        console.error('Error fetching inventory:', error);
      } finally {
        setIsLoadingInventory(false);
      }
    };
    
    fetchData();
  }, []);

  // Calculate totals from selected items + custom items
  const { totalCbm, itemCount, inventoryItems } = useMemo(() => {
    let cbm = 0;
    let count = 0;
    const items = [];
    
    // Add MovePro catalogue items
    inventoryCategories.forEach(cat => {
      (cat.options || []).forEach(opt => {
        const qty = selectedItems[opt.id] || 0;
        if (qty > 0) {
          cbm += (opt.volume || 0) * qty;
          count += qty;
          items.push({
            inventory_category_id: cat.id,
            inventory_option_id: opt.id,
            name: opt.name,
            qty,
            volume: opt.volume
          });
        }
      });
    });
    
    // Add custom items
    customItems.forEach(item => {
      cbm += (item.volume || 0) * item.qty;
      count += item.qty;
      items.push({
        name: item.name,
        qty: item.qty,
        volume: item.volume,
        is_custom: true
      });
    });
    
    return { totalCbm: cbm, itemCount: count, inventoryItems: items };
  }, [inventoryCategories, selectedItems, customItems]);

  // Calculate quote when relevant data changes
  const calculateQuote = useCallback(async () => {
    // Don't calculate if inventory was skipped or no items
    if (inventorySkipped || itemCount === 0 || !formData.pickupAddress.formatted_address || !formData.moveDate) {
      setQuote(null);
      return;
    }

    setIsCalculating(true);
    const heavyCount = heavyItems.reduce((sum, h) => sum + h.qty, 0);

    try {
      const response = await axios.post(`${API}/quote/calculate`, {
        inventory_items: inventoryItems,
        move_date: formData.moveDate,
        pickup_address: formData.pickupAddress.formatted_address,
        heavy_items_count: heavyCount
      });

      if (response.data.success) {
        setQuote(response.data);
      }
    } catch (error) {
      console.error('Error calculating quote:', error);
    } finally {
      setIsCalculating(false);
    }
  }, [inventoryItems, formData.pickupAddress, formData.moveDate, heavyItems, itemCount, inventorySkipped]);

  // Debounced quote calculation
  useEffect(() => {
    if (calculateTimeoutRef.current) {
      clearTimeout(calculateTimeoutRef.current);
    }

    calculateTimeoutRef.current = setTimeout(() => {
      calculateQuote();
    }, 500);

    return () => {
      if (calculateTimeoutRef.current) {
        clearTimeout(calculateTimeoutRef.current);
      }
    };
  }, [calculateQuote]);

  const handleItemChange = (item, qty) => {
    setSelectedItems(prev => ({
      ...prev,
      [item.id]: qty
    }));
  };

  const trackPhoneClick = (label) => {
    // Track phone clicks as GA4 events (not conversion events)
    // Phone call conversions are handled automatically by gtag's phone_conversion_number config
    if (window.gtag) {
      window.gtag('event', 'phone_click', {
        event_category: 'Phone',
        event_label: label
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email before submission
    const emailValidationError = validateEmail(formData.email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      return;
    }
    
    // Check if selected date is blocked
    if (blockedDates.includes(formData.moveDate)) {
      alert('This date is no longer available. Please select a different moving date.');
      return;
    }
    
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/quote/submit`, {
        full_name: formData.fullName,
        phone: formData.phone,
        email: formData.email,
        move_date: formData.moveDate,
        pickup_address: formData.pickupAddress,
        delivery_address: formData.deliveryAddress,
        inventory_items: inventorySkipped ? [] : inventoryItems,
        pickup_access: quoteMode === 'quick' ? [] : pickupAccess,
        delivery_access: quoteMode === 'quick' ? [] : deliveryAccess,
        heavy_items: quoteMode === 'quick' ? [] : heavyItems,
        notes: formData.notes,
        attribution: attribution,
        inventory_skipped: inventorySkipped || quoteMode === 'quick',
        quote_type: quoteMode === 'quick' ? 'quick_quote' : 'instant_estimate',
        move_size: quoteMode === 'quick' ? moveSize : null
      });
      
      // Set flag for conversion tracking - only fire on legitimate submissions
      sessionStorage.setItem('quote_submitted', 'true');
      
      // Navigate to thank you page (conversion fires there)
      navigate('/thank-you');
    } catch (error) {
      console.error('Error submitting quote:', error);
      // Check for blocked date error from backend
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('no longer available')) {
        alert(error.response.data.detail);
      } else {
        alert('Error submitting form. Please try calling us directly at 1800 233 023');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <>
      <Helmet>
        <title>{`${quoteMode === 'quick' ? 'Quick Quote Request' : 'Instant Moving Quote Calculator'} | From $130/hr | Thrifty Move Melbourne`}</title>
        <meta name="description" content="Get a Melbourne moving quote from $130/hr. Instant pricing calculator or quick quote request. 2 removalists included, GST included, 2-hour minimum." />
        <link rel="canonical" href="https://thrifty-move.com.au/quote" />
        <meta property="og:title" content="Get a Moving Quote | From $130/hr | Thrifty Move Melbourne" />
        <meta property="og:description" content="Get instant pricing or request a quick quote. Professional Melbourne removalists from $130/hr with 2 removalists included." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thrifty-move.com.au/quote" />
      </Helmet>

      <Header />

      {/* Fixed CTAs - only show when in a quote flow */}
      {quoteMode && (
        <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
          <a href={`tel:${mockData.business.phone.replace(/\s/g, '')}`} onClick={() => trackPhoneClick('Call Now - Sticky')}>
            <Button className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold px-6 py-4 rounded-full shadow-2xl">
              <Phone className="mr-2 w-5 h-5" />
              Call Now
            </Button>
          </a>
        </div>
      )}

      {/* Quote Mode Selection Screen */}
      {!quoteMode && (
        <>
          <section className="bg-gray-900 pt-28 md:pt-32 pb-8">
            <div className="container mx-auto px-4">
              <div className="max-w-3xl mx-auto text-center">
                <div className="inline-flex items-center bg-yellow-500/10 border border-yellow-500/30 px-3 py-1.5 rounded-full mb-3">
                  <div className="flex text-yellow-500 mr-2">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-current" />)}
                  </div>
                  <span className="text-yellow-500 font-semibold text-xs">{businessConfig.reviews.displayText}</span>
                </div>
                <h1 className="text-2xl md:text-4xl font-bold text-white mb-2">
                  Get Your Moving Quote
                </h1>
                <p className="text-base text-gray-300">
                  Choose how you'd like to get started
                </p>
              </div>
            </div>
          </section>

          <div className="bg-gray-50 py-6 md:py-10">
            <div className="container mx-auto px-4">
              <div className="max-w-2xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  
                  {/* Option 1: Instant Estimate (Primary) */}
                  <Card 
                    className="border-2 border-yellow-400 bg-white hover:shadow-xl transition-shadow cursor-pointer relative overflow-hidden"
                    onClick={() => setQuoteMode('instant')}
                    data-testid="quote-mode-instant"
                  >
                    <div className="absolute top-0 left-0 right-0 bg-yellow-500 text-gray-900 text-center text-xs font-bold py-1">
                      Most accurate
                    </div>
                    <CardContent className="pt-8 pb-4 px-4 md:px-6">
                      <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mb-3">
                        <Calculator className="w-6 h-6 text-yellow-600" />
                      </div>
                      <h2 className="text-lg font-bold text-gray-900 mb-1">Get an Instant Estimate</h2>
                      <p className="text-gray-600 text-sm mb-4">
                        Add your items and see your estimated hourly rate.
                      </p>
                      <Button className="w-full bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold py-2.5">
                        Build My Inventory
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Option 2: Quick Quote */}
                  <Card 
                    className="border-2 border-gray-200 bg-white hover:border-gray-300 hover:shadow-lg transition-all cursor-pointer"
                    onClick={() => {
                      setQuoteMode('quick');
                      setInventorySkipped(true);
                    }}
                    data-testid="quote-mode-quick"
                  >
                    <CardContent className="pt-4 pb-4 px-4 md:px-6">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-3">
                        <Phone className="w-6 h-6 text-blue-600" />
                      </div>
                      <h2 className="text-lg font-bold text-gray-900 mb-1">Request a Quick Quote</h2>
                      <p className="text-gray-600 text-sm mb-4">
                        Short on time? Send us the basics and we'll call you.
                      </p>
                      <Button variant="outline" className="w-full border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white font-bold py-2.5">
                        Quick Quote
                      </Button>
                    </CardContent>
                  </Card>

                </div>

                <p className="text-center text-gray-500 text-xs mt-4">
                  Not sure? Start with a Quick Quote — we'll help from there.
                </p>
              </div>
            </div>
          </div>
          <Footer />
        </>
      )}

      {/* Quick Quote Flow */}
      {quoteMode === 'quick' && (
        <>
          <section className="bg-gray-900 pt-32 pb-8">
            <div className="container mx-auto px-4">
              <div className="max-w-3xl mx-auto text-center">
                <button 
                  onClick={() => {
                    setQuoteMode(null);
                    setInventorySkipped(false);
                    setMoveSize('');
                  }}
                  className="text-gray-400 hover:text-white text-sm mb-4 inline-flex items-center gap-1"
                  data-testid="back-to-choice"
                >
                  ← Back to quote options
                </button>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
                  Quick Quote Request
                </h1>
                <p className="text-lg text-gray-300">
                  Tell us about your move and we'll contact you with pricing
                </p>
              </div>
            </div>
          </section>

          <div className="bg-gray-50 py-8">
            <div className="container mx-auto px-4">
              <div className="max-w-2xl mx-auto">
                <form onSubmit={handleSubmit} className="space-y-6">
                  
                  {/* Move Details */}
                  <Card className="border-0 shadow-lg">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <span className="w-7 h-7 rounded-full bg-yellow-500 text-white flex items-center justify-center text-sm font-bold">1</span>
                        Move Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Address</label>
                        <AddressAutocomplete
                          id="quick-pickup"
                          value={formData.pickupAddress.formatted_address}
                          onChange={(addr) => setFormData(prev => ({ ...prev, pickupAddress: addr }))}
                          placeholder="Start typing pickup address..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Address</label>
                        <AddressAutocomplete
                          id="quick-delivery"
                          value={formData.deliveryAddress.formatted_address}
                          onChange={(addr) => setFormData(prev => ({ ...prev, deliveryAddress: addr }))}
                          placeholder="Start typing delivery address..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Moving Date</label>
                        <input
                          type="date"
                          value={formData.moveDate}
                          onChange={(e) => setFormData(prev => ({ ...prev, moveDate: e.target.value }))}
                          min={new Date().toISOString().split('T')[0]}
                          className={`w-full px-4 py-3 rounded-lg border ${
                            blockedDates.includes(formData.moveDate) 
                              ? 'border-red-500 bg-red-50' 
                              : 'border-gray-200'
                          } focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 focus:outline-none`}
                          data-testid="quick-move-date-input"
                        />
                        {blockedDates.includes(formData.moveDate) && (
                          <p className="text-red-500 text-sm mt-1">This date is not available. Please select another date.</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Approximate Move Size */}
                  <Card className="border-0 shadow-lg">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <span className="w-7 h-7 rounded-full bg-yellow-500 text-white flex items-center justify-center text-sm font-bold">2</span>
                        Approximate Move Size
                      </CardTitle>
                      <p className="text-sm text-gray-500 mt-1">This helps us understand your move — we'll confirm the details with you.</p>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {moveSizeOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => setMoveSize(option.value)}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${
                              moveSize === option.value 
                                ? 'border-yellow-500 bg-yellow-50' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            data-testid={`move-size-${option.value}`}
                          >
                            <div className="font-medium text-gray-900 text-sm">{option.label}</div>
                            <div className="text-xs text-gray-500">{option.description}</div>
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Contact Details */}
                  <Card className="border-0 shadow-lg">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <span className="w-7 h-7 rounded-full bg-yellow-500 text-white flex items-center justify-center text-sm font-bold">3</span>
                        Your Contact Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                        <input
                          type="text"
                          value={formData.fullName}
                          onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                          placeholder="Your name"
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="04XX XXX XXX"
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={handleEmailChange}
                          onBlur={handleEmailBlur}
                          placeholder="your@email.com"
                          required
                          className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:outline-none ${
                            emailError 
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-200' 
                              : 'border-gray-200 focus:border-yellow-500 focus:ring-yellow-200'
                          }`}
                          data-testid="quick-quote-email-input"
                        />
                        {emailError && (
                          <p className="mt-1 text-sm text-red-600" data-testid="quick-quote-email-error">{emailError}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes (optional)</label>
                        <textarea
                          value={formData.notes}
                          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                          placeholder="Anything else we should know about your move?"
                          rows={2}
                          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 focus:outline-none"
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Quick Quote Summary */}
                  <Card className="border-2 border-blue-400 bg-blue-50">
                    <CardContent className="pt-6">
                      <h3 className="font-bold text-lg text-gray-900 mb-3 flex items-center gap-2">
                        <Phone className="w-5 h-5 text-blue-600" />
                        Quick Quote Summary
                      </h3>
                      <div className="bg-white rounded-lg p-4 mb-4">
                        <p className="font-semibold text-gray-900">Rate to be confirmed</p>
                        <p className="text-sm text-gray-600 mt-1">
                          We'll confirm the appropriate truck capacity and hourly rate after reviewing your move details.
                        </p>
                      </div>
                      <p className="text-sm text-gray-600">
                        <strong>What happens next:</strong> Our team will call you to discuss your move, confirm inventory requirements, and provide accurate pricing.
                      </p>
                    </CardContent>
                  </Card>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting || !formData.pickupAddress.formatted_address || !formData.deliveryAddress.formatted_address || !formData.moveDate || !formData.fullName || !formData.phone || !formData.email || emailError || blockedDates.includes(formData.moveDate)}
                    className="w-full bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold text-xl py-6 disabled:opacity-50"
                    data-testid="submit-quick-quote-btn"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Submitting...
                      </span>
                    ) : (
                      'Submit Quick Quote Request'
                    )}
                  </Button>

                  <p className="text-center text-gray-500 text-sm">
                    Want a more accurate estimate? <button type="button" onClick={() => { setQuoteMode('instant'); setInventorySkipped(false); setMoveSize(''); }} className="text-yellow-600 hover:text-yellow-700 font-medium underline">Build your inventory instead</button>
                  </p>
                </form>
              </div>
            </div>
          </div>
          <Footer />
        </>
      )}

      {/* Instant Estimate Flow (Existing Detailed Quote) */}
      {quoteMode === 'instant' && (
        <>
          {/* Hero Section */}
          <section className="bg-gray-900 pt-32 pb-8">
            <div className="container mx-auto px-4">
              <div className="max-w-3xl mx-auto text-center">
                <button 
                  onClick={() => {
                    setQuoteMode(null);
                    setInventorySkipped(false);
                  }}
                  className="text-gray-400 hover:text-white text-sm mb-4 inline-flex items-center gap-1"
                  data-testid="back-to-choice-instant"
                >
                  ← Back to quote options
                </button>
                <div className="inline-flex items-center bg-yellow-500/10 border border-yellow-500/30 px-4 py-2 rounded-full mb-4">
                  <div className="flex text-yellow-500 mr-2">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                  </div>
                  <span className="text-yellow-500 font-semibold text-sm">{businessConfig.reviews.displayText}</span>
                </div>

                <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
                  Instant Moving Quote
                </h1>
                <p className="text-lg text-gray-300">
                  Select your items and get real-time pricing
                </p>
              </div>
            </div>
          </section>

          <div className="bg-gray-50 py-8">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto">
                
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                  {/* Left Column - Form (3 cols) */}
                  <div className="lg:col-span-3 space-y-6">
                
                {/* Step 1: Addresses */}
                <Card className="border-0 shadow-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <span className="w-7 h-7 rounded-full bg-yellow-500 text-white flex items-center justify-center text-sm font-bold">1</span>
                      Move Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <AddressAutocomplete
                      id="pickup"
                      value={formData.pickupAddress}
                      onChange={(value) => setFormData({ ...formData, pickupAddress: value })}
                      placeholder="Start typing pickup address..."
                      label="Pickup Address"
                    />
                    
                    <AddressAutocomplete
                      id="delivery"
                      value={formData.deliveryAddress}
                      onChange={(value) => setFormData({ ...formData, deliveryAddress: value })}
                      placeholder="Start typing delivery address..."
                      label="Delivery Address"
                    />

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Moving Date *
                      </label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="date"
                          required
                          value={formData.moveDate}
                          onChange={(e) => {
                            const newDate = e.target.value;
                            setFormData({ ...formData, moveDate: newDate });
                            // Warn if date is blocked
                            if (blockedDates.includes(newDate)) {
                              alert('This date is not available. Please choose another date.');
                            }
                          }}
                          min={today}
                          className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 focus:outline-none ${
                            blockedDates.includes(formData.moveDate) 
                              ? 'border-red-500 bg-red-50' 
                              : 'border-gray-300'
                          }`}
                          data-testid="move-date-input"
                        />
                      </div>
                      {blockedDates.includes(formData.moveDate) && (
                        <p className="text-sm text-red-600 mt-1">This date is not available. Please choose another date.</p>
                      )}
                    </div>

                    {/* Access Info */}
                    {accessTags.length > 0 && (
                      <AccessInfoSelector
                        accessTags={accessTags}
                        pickupAccess={pickupAccess}
                        setPickupAccess={setPickupAccess}
                        deliveryAccess={deliveryAccess}
                        setDeliveryAccess={setDeliveryAccess}
                      />
                    )}
                  </CardContent>
                </Card>

                {/* Step 2: Inventory */}
                <Card className="border-0 shadow-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <span className="w-7 h-7 rounded-full bg-yellow-500 text-white flex items-center justify-center text-sm font-bold">2</span>
                      What Are You Moving?
                    </CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      Add your items for a more accurate estimate, or skip this step and we'll confirm the details with you.
                    </p>
                  </CardHeader>
                  <CardContent>
                    {/* Inventory Skipped Toggle */}
                    {inventorySkipped ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Phone className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-blue-900">We'll contact you to confirm your inventory</p>
                            <p className="text-sm text-blue-700 mt-1">
                              Our team will call to discuss your move and provide accurate pricing based on your requirements.
                            </p>
                            <button
                              type="button"
                              onClick={() => setInventorySkipped(false)}
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium mt-2 underline"
                              data-testid="add-inventory-btn"
                            >
                              I'd like to add my inventory instead
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : isLoadingInventory ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-8 h-8 animate-spin text-yellow-600" />
                        <span className="ml-2 text-gray-600">Loading inventory...</span>
                      </div>
                    ) : inventoryCategories.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>Inventory catalogue unavailable. Please call for a quote.</p>
                      </div>
                    ) : (
                      <>
                        {/* Search */}
                        <div className="relative mb-4">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search items..."
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 focus:outline-none"
                            data-testid="inventory-search"
                          />
                        </div>

                        {/* Live Summary */}
                        <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg mb-4">
                          <div className="flex items-center gap-4">
                            <div>
                              <span className="text-sm text-gray-600">Items</span>
                              <p className="font-bold text-gray-900" data-testid="item-count">{itemCount}</p>
                            </div>
                            <div className="border-l border-yellow-300 pl-4">
                              <span className="text-sm text-gray-600">Est. Volume</span>
                              <p className="font-bold text-gray-900" data-testid="volume-display">{totalCbm.toFixed(1)} m³</p>
                            </div>
                          </div>
                        </div>

                        {/* Categories */}
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {inventoryCategories.map(category => (
                            <InventoryCategory
                              key={category.id}
                              category={category}
                              selectedItems={selectedItems}
                              onItemChange={handleItemChange}
                              searchQuery={searchQuery}
                            />
                          ))}
                        </div>

                        {/* Selected Items Summary */}
                        <SelectedItemsSummary
                          categories={inventoryCategories}
                          selectedItems={selectedItems}
                          onItemChange={handleItemChange}
                        />
                        
                        {/* Custom Items Section */}
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h4 className="font-semibold text-gray-800">Can't find an item?</h4>
                              <p className="text-sm text-gray-500">Add custom items with estimated volume</p>
                            </div>
                            {!showCustomItemForm && (
                              <button
                                type="button"
                                onClick={() => setShowCustomItemForm(true)}
                                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-yellow-700 bg-yellow-100 rounded-lg hover:bg-yellow-200 transition-colors"
                                data-testid="add-custom-item-btn"
                              >
                                <Plus className="w-4 h-4" />
                                Add Custom Item
                              </button>
                            )}
                          </div>
                          
                          {showCustomItemForm && (
                            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mb-3">
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Item Name *</label>
                                  <input
                                    type="text"
                                    value={newCustomItem.name}
                                    onChange={(e) => setNewCustomItem({ ...newCustomItem, name: e.target.value })}
                                    placeholder="e.g., Antique Cabinet"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 focus:outline-none"
                                    data-testid="custom-item-name"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Quantity</label>
                                  <input
                                    type="number"
                                    min="1"
                                    max="50"
                                    value={newCustomItem.qty}
                                    onChange={(e) => setNewCustomItem({ ...newCustomItem, qty: Math.max(1, parseInt(e.target.value) || 1) })}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 focus:outline-none"
                                    data-testid="custom-item-qty"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Est. Volume (m³)</label>
                                  <input
                                    type="number"
                                    min="0.1"
                                    max="10"
                                    step="0.1"
                                    value={newCustomItem.volume}
                                    onChange={(e) => setNewCustomItem({ ...newCustomItem, volume: Math.max(0.1, parseFloat(e.target.value) || 0.5) })}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 focus:outline-none"
                                    data-testid="custom-item-volume"
                                  />
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 mb-3">
                                <Info className="w-3 h-3 inline mr-1" />
                                Tip: A standard dining chair is ~0.3m³, a 2-seater sofa is ~1.5m³, a fridge is ~1m³
                              </p>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (newCustomItem.name.trim()) {
                                      setCustomItems([...customItems, { ...newCustomItem, id: Date.now() }]);
                                      setNewCustomItem({ name: '', qty: 1, volume: 0.5 });
                                      setShowCustomItemForm(false);
                                    }
                                  }}
                                  disabled={!newCustomItem.name.trim()}
                                  className="px-4 py-2 text-sm font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  data-testid="save-custom-item-btn"
                                >
                                  Add Item
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowCustomItemForm(false);
                                    setNewCustomItem({ name: '', qty: 1, volume: 0.5 });
                                  }}
                                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                          
                          {/* Custom Items List */}
                          {customItems.length > 0 && (
                            <div className="space-y-2">
                              <h5 className="text-sm font-medium text-gray-700">Custom Items Added:</h5>
                              {customItems.map(item => (
                                <div key={item.id} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg border border-blue-100">
                                  <div className="flex items-center gap-2">
                                    <Package className="w-4 h-4 text-blue-600" />
                                    <span className="text-sm font-medium text-gray-800">{item.name}</span>
                                    <span className="text-xs text-gray-500">x{item.qty} ({(item.volume * item.qty).toFixed(1)}m³)</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setCustomItems(customItems.filter(i => i.id !== item.id))}
                                    className="p-1 text-red-500 hover:bg-red-100 rounded"
                                    data-testid={`remove-custom-item-${item.id}`}
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Skip Inventory Option */}
                        <div className="mt-6 pt-4 border-t border-gray-200">
                          <button
                            type="button"
                            onClick={() => {
                              setInventorySkipped(true);
                              setSelectedItems({});
                              setCustomItems([]);
                            }}
                            className="w-full text-center text-sm text-gray-500 hover:text-gray-700 py-2"
                            data-testid="skip-inventory-btn"
                          >
                            Not sure what you're moving? <span className="font-medium underline">Skip inventory — we'll contact you</span>
                          </button>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Step 3: Heavy Items */}
                <Card className="border-0 shadow-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <span className="w-7 h-7 rounded-full bg-yellow-500 text-white flex items-center justify-center text-sm font-bold">3</span>
                      Heavy Items
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <HeavyItemsSelector heavyItems={heavyItems} setHeavyItems={setHeavyItems} />
                  </CardContent>
                </Card>

                {/* Step 4: Contact Details */}
                <Card className="border-0 shadow-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <span className="w-7 h-7 rounded-full bg-yellow-500 text-white flex items-center justify-center text-sm font-bold">4</span>
                      Your Contact Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">
                            Full Name *
                          </label>
                          <input
                            type="text"
                            required
                            value={formData.fullName}
                            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 focus:outline-none"
                            placeholder="Your name"
                            data-testid="name-input"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">
                            Phone *
                          </label>
                          <input
                            type="tel"
                            required
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 focus:outline-none"
                            placeholder="04XX XXX XXX"
                            data-testid="phone-input"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          Email *
                        </label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleEmailChange}
                          onBlur={handleEmailBlur}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-1 focus:outline-none ${
                            emailError 
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                              : 'border-gray-300 focus:border-yellow-500 focus:ring-yellow-500'
                          }`}
                          placeholder="your@email.com"
                          data-testid="email-input"
                        />
                        {emailError && (
                          <p className="mt-1 text-sm text-red-600" data-testid="email-error">{emailError}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          Additional Notes (Optional)
                        </label>
                        <textarea
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 focus:outline-none"
                          placeholder="Special instructions, fragile items, etc."
                          data-testid="notes-input"
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting || (!inventorySkipped && itemCount === 0) || !formData.pickupAddress.formatted_address || !formData.deliveryAddress.formatted_address || !formData.moveDate || !formData.email || emailError}
                        className="w-full bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold text-xl py-6 disabled:opacity-50"
                        data-testid="submit-quote-btn"
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>

                      <p className="text-center text-sm text-gray-500">
                        We'll confirm your quote within 30 minutes during business hours
                      </p>
                    </form>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - Quote Summary (2 cols) */}
              <div className="lg:col-span-2">
                <div className="lg:sticky lg:top-24 space-y-4">
                  <QuoteSummary 
                    quote={quote} 
                    isLoading={isCalculating}
                    itemCount={itemCount}
                    totalCbm={totalCbm}
                    inventorySkipped={inventorySkipped}
                  />

                  {/* Pricing Tiers Info */}
                  <Card className="border-2 border-gray-200">
                    <CardContent className="pt-6">
                      <h3 className="font-bold text-lg text-gray-900 mb-4">Our Pricing</h3>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">4-6 Tonne (0–22 m³)</span>
                          <span className="font-semibold">$130/hour</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">8-10 Tonne (22–32 m³)</span>
                          <span className="font-semibold">$140/hour</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">12+ Tonne (32–52 m³)</span>
                          <span className="font-semibold">From $187/hour</span>
                        </div>
                        <hr className="my-2" />
                        <p className="text-xs text-gray-500">
                          All rates include 2 removalists and GST. Minimum 2 hours.
                        </p>
                        <p className="text-xs text-orange-600 font-medium">
                          Sunday: +$10/hour
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Trust Points */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 text-gray-600 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span>No Hidden Fees</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600 text-sm">
                      <Clock className="w-4 h-4 text-green-600" />
                      <span>Fast Response</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600 text-sm">
                      <Shield className="w-4 h-4 text-green-600" />
                      <span>Fully Insured</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600 text-sm">
                      <Truck className="w-4 h-4 text-green-600" />
                      <span>Local Team</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

          <Footer />
        </>
      )}
    </>
  );
}
